<template>
	<StandardModal
		open
		small
		ref="modal"
		class="external-id-modal"
		:title="$t('admin_people_external_id_label')"
		:confirmText="$t('common_button_save')"
		:confirmProps="{ disabled: !canSave, loading }"
		:cancelProps="{ disabled: loading }"
		@confirm="save"
		@cancel="$emit('close')"
	>
		<div class="modal-wrapper">
			<p class="external-id-desc">
				{{ $t('admin_people_external_id_modal_desc') }}
			</p>
			<FormItem
				:label="$t('admin_people_external_id_input_label')"
				:error="!!error"
				:message="error"
			>
				<Input
					v-model="value"
					autoFocus
					selectAll
					data-cy="external-id-input"
					@enter="onEnter"
				/>
			</FormItem>
			<div v-if="hasExisting" class="remove-row">
				<a
					class="remove-link"
					role="button"
					tabindex="0"
					data-cy="external-id-remove"
					@click="remove"
					@keydown.enter="remove"
				>
					<TrashCanIcon type="regular" />
					{{ $t('admin_people_external_id_remove') }}
				</a>
			</div>
		</div>
	</StandardModal>
</template>

<script>
import { Input, StandardModal, FormItem } from '@/components/common'
import { TrashCanIcon } from '@/icons'
import { patch } from '../common'
import { storage } from '@/helpers/utils'

export default {
	name: 'ExternalIdModal',
	components: { StandardModal, FormItem, Input, TrashCanIcon },
	props: {
		person: { type: Object, required: true },
	},
	data() {
		return {
			value: this.person?.external_id ?? '',
			loading: false,
			error: null,
		}
	},
	emits: ['success', 'close'],
	watch: {
		value() {
			this.error = null
		},
	},
	computed: {
		trimmedValue() {
			return this.value?.trim() ?? ''
		},
		currentValue() {
			return this.person?.external_id ?? ''
		},
		hasChanged() {
			return this.trimmedValue !== this.currentValue
		},
		canSave() {
			return this.hasChanged
		},
		hasExisting() {
			return !!this.person?.external_id
		},
	},
	methods: {
		onEnter() {
			if (this.canSave) this.save()
		},
		save() {
			return this.submit(this.trimmedValue || null)
		},
		remove() {
			this.value = ''
		},
		async submit(external_id) {
			if (this.loading) return
			this.loading = true
			this.error = null
			try {
				const user = await patch(`/organization/users/${this.person.id}`, {
					external_id,
				})
				storage.set('org_mgmt_changed_people', true)
				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
				this.$emit('success', user)
			} catch (e) {
				const fieldError = e.response?.data?.errors?.external_id?.[0]?.message
				if (fieldError) {
					this.error = fieldError
					return
				}
				this.$refs.modal.$alert.error(this.$t('common_api_error_save'))
			} finally {
				// reset on success too, so the modal doesn't depend on the
				// parent unmounting it to clear the spinner
				this.loading = false
			}
		},
	},
}
</script>

<style lang="scss">
@import 'src/sass/variables';

.external-id-modal {
	.modal-wrapper {
		padding: 24px;
	}
	.external-id-desc {
		margin: 0 0 24px;
		text-align: center;
		font-size: 14px;
		color: var(--neutral-600);
	}
	.remove-row {
		@include flex;
		justify-content: flex-end;
		margin-top: 8px;
	}
	.remove-link {
		@include flex(6px);
		color: var(--red-200);
		font-family: Avenir-Heavy;
		font-size: 14px;
		cursor: pointer;
		.pronto-icon {
			font-size: 14px;
		}
		&:hover {
			color: var(--red-300);
		}
	}
}
</style>
